import React, { useEffect, useRef, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Subject, BehaviorSubject } from "rxjs";
import xstream from "state-x-stream";
import HelperText from "../../../atoms/HelperText";
import Toggle from "../../../atoms/Toggle";
import beeswarm from "./beeswarmDiagram";
import { expandTagsPerYear, filterData, extractCategories } from "./parseData";
import HoverTooltip from "../../../atoms/Tooltip/HoverTooltip";
import Btn from "../../../atoms/Btn";
import ImperativeTooltip from "../../../atoms/Tooltip/ImperativeTooltip";
import { TAG_TITLES } from "../../constants/tagConstants";
import Tag from "../../models/Tag";

function controller({ props$ }) {
  const clickItem$ = new BehaviorSubject(null);

  return {
    clickItem: clickItem$,
    onElementSelected: (x) => props$.value.onElementSelected(x),
    setClickItem: (x) => {
      clickItem$.next(x);
    },
    setTooltipData: (x) => {
      if (!x) props$.value.onElementSelected(null);
      clickItem$.next(x);
    },
  };
}

function Beeswarm({
  data: propData,
  onElementSelected,
  selectedItem,
  setTooltipData,
  setClickItem,
  clickItem,
}) {
  const allCategoryKey = "All";
  const [allTags, setAllTags] = useState([]);
  const [filteredTags, setFilteredTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [unboundTagsLength, setUnboundTagsLength] = useState(0);
  const [unboundUniqueTagsLength, setUnboundUniqueTagsLength] = useState(0);
  const [showOriginalTagColor, setShowOriginalTagColor] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(allCategoryKey);
  const [isTagsExpanded, setIsTagsExpanded] = useState(false);
  const [legend, setLegend] = useState(null);
  const resize$ = useRef(new BehaviorSubject(null));
  const highlight$ = useRef(new Subject());
  const isOpacityScale$ = useRef(new BehaviorSubject(true));
  const select$ = useRef(new BehaviorSubject());
  const filteredData$ = useRef(new BehaviorSubject([]));
  const yAxis$ = useRef(new BehaviorSubject("tagType"));
  const [tagFilter, setTagFilter] = useState("");

  const bubbleRef = useRef();
  const xAxisRef = useRef();
  const anchorRef = useRef();

  const [uniqueTags, setUniqueTags] = useState([]);

  const filteredUniqueTags = useMemo(() => {
    const re = new RegExp(`^${tagFilter}`, "gi");
    return uniqueTags
      .filter((x) => (x.tag.normalized || "").match(re))
      .sort((a, b) => b.totalCount - a.totalCount);
  }, [tagFilter, uniqueTags]);

  useEffect(() => {
    const categoryYAxis = "tagType";
    const idYAxis = "typeKey";
    yAxis$.current.next(isTagsExpanded ? idYAxis : categoryYAxis);
  }, [isTagsExpanded, uniqueTags]);

  useEffect(() => {
    filteredData$.current.next(filteredTags);
  }, [filteredTags]);

  useEffect(() => {
    isOpacityScale$.current.next(showOriginalTagColor);
  }, [showOriginalTagColor]);

  useEffect(() => {
    const {
      tags,
      uniqueTags: unique,
      unboundTags,
      unboundUniqueTags,
    } = filterData(
      allTags,
      selectedCategory === allCategoryKey ? null : selectedCategory
    );

    setFilteredTags(tags);
    setUniqueTags(unique);
    setUnboundTagsLength(unboundTags.length);
    setUnboundUniqueTagsLength(unboundUniqueTags.length);
  }, [allTags, selectedCategory]);

  useEffect(() => {
    const expandedTags = expandTagsPerYear(propData);
    const cats = extractCategories(propData);
    setCategories([allCategoryKey, ...(cats || [])]);
    setAllTags(expandedTags);
  }, [propData]);

  useEffect(() => {
    const destroyed$ = new Subject();
    const handleResize = () => resize$.current.next(null);

    beeswarm({
      margin: { top: 30, bottom: 0, right: 30, left: 160 },
      data$: filteredData$.current,
      element: bubbleRef.current,
      xAxisEl: xAxisRef.current,
      resize$: resize$.current,
      highlight$: highlight$.current,
      select$: select$.current,
      yAxis$: yAxis$.current,
      isOpacityScale$: isOpacityScale$.current,
      destroyed$,
      onLegendUpdated: setLegend,
      getY: (x) => x.tag.tagType,
      getX: (x) => x.tag.year,
      getSize: (x) => x.tag.count,
      getColor: (x) => x.totalCount,
      onClick: (d) => setTooltipData(d),
    });

    window.addEventListener("resize", handleResize);

    return () => {
      destroyed$.next();
      window.removeEventListener("resize", handleResize);
    };
  }, [propData, setTooltipData]);

  useEffect(() => {
    select$.current.next(selectedItem);
  }, [selectedItem]);

  return categories ? (
    <div className="tw-border tw-rounded-lg tw-overflow-hidden tw-text-xs tw-shadow-lg">
      <div className="tw-flex">
        <div className="tw-p-1 tw-px-2 tw-relative tw-flex-grow">
          <div className="tw-py-1 tw-flex w-full">
            <span>Tag Categories:</span>
            <div className="tw-flex-grow" />
            <div className="tw-inline-block tw-mx-3">
              <Toggle
                sizeClass="tw-w-6"
                isChecked={isTagsExpanded}
                onChange={() => setIsTagsExpanded(!isTagsExpanded)}
              >
                <span className="tw-px-1">Show tags individually</span>
              </Toggle>
            </div>

            <div className="tw-inline-block tw-mx-3">
              <Toggle
                sizeClass="tw-w-6"
                isChecked={showOriginalTagColor}
                onChange={() => setShowOriginalTagColor(!showOriginalTagColor)}
              >
                <span className="tw-px-1">Color code by Tag Type</span>
              </Toggle>
            </div>
          </div>

          <div className="tw-text-xs tw-flex tw-flex-wrap">
            {categories.map((x) => {
              const inputId = String(x);
              return (
                <label
                  key={x}
                  className="tw-pr-2 tw-my-1 tw-flex tw-items-center tw-mb-0 tw-cursor-pointer"
                >
                  <input
                    type="radio"
                    checked={selectedCategory === x}
                    onChange={(e) => {
                      setIsTagsExpanded(e.target.value !== allCategoryKey);
                      setSelectedCategory(e.target.value);
                    }}
                    name="category"
                    id={inputId}
                    value={x}
                  />
                  <div className="tw-px-1 tw-capitalize">
                    {TAG_TITLES[x.toLowerCase()] || x}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      <div className="tw-flex bees-chart tw-border-t">
        <div className="tw-relative tw-flex-grow ">
          <div className="tw-absolute tw-inset-0">
            <div className="tw-flex tw-flex-col tw-h-full tw-items-stretch">
              {/* TITLE */}
              <div className="tw-p-3 tw-flex-shrink-0 tw-text-xs tw-text-center">
                <div className="tw-text-blueGray-500 ">
                  {`Showing ${uniqueTags.length} out of ${unboundUniqueTagsLength} unique tags. (${filteredTags.length} out of ${unboundTagsLength} data points)`}
                </div>
              </div>
              {/* CHART */}
              <div
                className="tw-flex-grow tw-overflow-auto tw-border-t tw-border-b tw-shadow-inner tw-relative"
                onMouseMove={(e) => {
                  const { offsetX, offsetY } = e.nativeEvent;
                  const el = anchorRef.current;
                  el.style.top = `${offsetY}px`;
                  el.style.left = `${offsetX}px`;
                }}
              >
                <div ref={bubbleRef} className="tw-overflow-auto" />
                <div
                  className="tw-absolute tw-w-0 tw-h-0 tw-pointer-events-none"
                  ref={anchorRef}
                />
              </div>
              {!filteredTags?.length ? (
                <div className="tw-absolute tw-bg-white tw-inset-0 tw-h-full tw-flex tw-justify-center tw-items-center">
                  No data found
                </div>
              ) : null}
              {/* X AXIS */}
              <div
                ref={xAxisRef}
                className="tw-h-8 tw-flex-shrink-0 tw-overflow-hidden"
              />
              {/* COLOR LEGEND */}
              {showOriginalTagColor || !filteredTags?.length ? null : (
                <div className="tw-flex tw-items-center tw-text-xs tw-pb-2 tw-justify-center">
                  <div className="tw-text-right tw-font-semibold tw-text-blueGray-600 tw-px-6 tw-w-36">
                    Color scale
                    <HelperText
                      title="Timeline Info"
                      RenderContent={() => (
                        <div className="font-semibold">
                          Denotes the number of publications that mention a tag
                          overall.
                        </div>
                      )}
                    />
                  </div>
                  <div className="tw-flex-grow tw-text-xs tw-text-blueGray-500 tw-px-6">
                    {legend ? (
                      <div className="tw-w-full tw-text-left tw-flex tw-rounded-full tw-overflow-hidden">
                        {legend
                          .filter((x) => x[0] % 1 === 0)
                          .map((x) => (
                            <div
                              key={x[1]}
                              style={{ backgroundColor: x[1] }}
                              className="tw-flex-grow tw-text-3xs flex-basis-0 tw-text-center tw-font-bold tw-py-1 tw-text-white"
                              data-tooltip={`mentioned in ${x[0]} Publications in total`}
                            >
                              {x[0]}
                            </div>
                          ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ height: "calc(100vh - 300px)", minHeight: "300px" }} />
        {isTagsExpanded || !filteredTags?.length ? null : (
          <div
            className="tw-w-64 tw-overflow-auto tw-text-xs tw-border-l tw-flex tw-flex-col"
            style={{ height: "calc(100vh - 300px)", minHeight: "300px" }}
          >
            <div className="tw-p-3">
              <input
                placeholder="filter"
                type="text"
                className="tw-m-2 tw-p-2 tw-border"
                style={{ width: "calc(100% - 20px)" }}
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
              />
            </div>
            <div className="tw-flex-grow tw-overflow-auto tw-shadow-inner">
              {filteredUniqueTags.map((x) => (
                <HoverTooltip
                  key={x.tag.typeKey}
                  className="tag-tooltip"
                  ContentRenderer={() => {
                    return (
                      <div className="tw-p-1 prevent-tooltip-close">
                        <div className="tw-flex tw-items-center">
                          <span className="tw-font-semibold">
                            {x.normalized}
                          </span>
                          <span className="tw-w-2" />
                          <span className="tw-opacity-70 tw-capitalize">
                            ({TAG_TITLES[x.tag.tagType] || x.tag.tagType})
                          </span>
                        </div>
                        <div className="tw-flex">
                          <span>Mentioned in {x.totalCount} publications</span>
                        </div>
                        <div>Click to see publications</div>
                      </div>
                    );
                  }}
                >
                  <div>
                    <div
                      onMouseLeave={() => highlight$.current.next(null)}
                      onMouseEnter={() => highlight$.current.next(x)}
                      onMouseMove={() => highlight$.current.next(x)}
                      onClick={() => {
                        if (selectedItem?.tag?.typeKey === x?.tag?.typeKey) {
                          onElementSelected(null);
                        } else {
                          onElementSelected(x?.tag.clone().setYear(null));
                        }
                      }}
                      className={`hover:tw-bg-blueGray-200 tw-flex tw-p-2 tw-pr-6 tw-cursor-pointer ${
                        selectedItem?.tag?.key === x?.tag?.key
                          ? "tw-bg-blueGray-300"
                          : ""
                      }`}
                    >
                      <div className="tw-w-min-0 tw-flex-grow tw-truncate">
                        {x.tag.normalized}{" "}
                      </div>

                      <span className="tw-text-blueGray-500 tw-font-bold tw-text-2xs">
                        ({x.totalCount})
                      </span>
                    </div>
                  </div>
                </HoverTooltip>
              ))}
            </div>
          </div>
        )}
      </div>

      {clickItem ? (
        <ImperativeTooltip
          onClose={() => setClickItem(null)}
          anchor={anchorRef.current}
          zIndex={10006}
          className="tooltip-default prevent-tooltip-close"
          ContentRenderer={() => {
            return (
              <div className="tw-p-2">
                <div className="tw-flex tw-items-center">
                  <span className="tw-font-semibold">
                    {clickItem.tag.normalized}
                  </span>
                  <span className="tw-w-2" />
                  <span className="tw-opacity-70 tw-capitalize">
                    (
                    {TAG_TITLES[clickItem.tag.tagType] || clickItem.tag.tagType}
                    )
                  </span>
                </div>
                <div className="tw-flex tw-items-center">
                  <span>
                    Mentioned in {clickItem.tag.count} publications in{" "}
                    {clickItem.tag.year}
                  </span>
                </div>
                <div>
                  Click
                  <Btn
                    className="tw-px-1 tw-text-center tw-opacity-70 tw-text-primary"
                    onSubmit={() => {
                      onElementSelected(clickItem.tag);
                    }}
                  >
                    <div>here</div>
                  </Btn>
                  to filter publications ({clickItem.tag.count})
                </div>
              </div>
            );
          }}
        />
      ) : null}
    </div>
  ) : null;
}

Beeswarm.propTypes = {
  setClickItem: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})),
  onElementSelected: PropTypes.func,
  selectedItem: PropTypes.shape({
    tag: PropTypes.instanceOf(Tag),
  }),
  setTooltipData: PropTypes.func.isRequired,
  clickItem: PropTypes.shape({
    tag: PropTypes.instanceOf(Tag),
    pointId: PropTypes.string,
    id: PropTypes.string,
    normalized: PropTypes.string,
    tagType: PropTypes.string,
    count: PropTypes.number,
    year: PropTypes.number,
  }),
};

Beeswarm.defaultProps = {
  clickItem: null,
  data: [],
  onElementSelected: () => {},
  selectedItem: null,
};

export default xstream(controller)(Beeswarm);
